/**
 * Per-tab (sessionStorage) state and navigation helpers shared by the auth
 * routes. Everything here must survive the full-page round trips that
 * WorkOS sign-in/sign-out and the reload-recovery below perform.
 *
 * sessionStorage can be unavailable or throw under strict browser privacy
 * settings and some embedded webviews, so every access is guarded — the auth
 * routes render outside the app's error boundaries and must never crash on it.
 */

export const AUTH_REDIRECT_STORAGE_KEY = "latend.auth.redirectTo";
export const AUTH_RELOAD_RECOVERY_STORAGE_KEY = "latend.auth.reloadRecoveryAt";

/**
 * A reload-recovery (see `login.tsx`) is allowed once per cooldown window. If
 * the tab lands on `/auth/login` with a stale session again inside the window,
 * a reload evidently does not fix it and the page falls through to sign-out.
 */
export const RELOAD_RECOVERY_COOLDOWN_MS = 2 * 60 * 1000;

const PATH_NORMALIZATION_ORIGIN = "https://app.invalid";

/**
 * Normalize an untrusted `redirect` value to a same-origin absolute app path.
 * The value is parsed the way the browser will parse it (so `//host`, `/\host`,
 * control characters the URL parser strips, absolute URLs and `javascript:` all
 * collapse to `/`), which makes it safe to hand to `window.location`.
 */
export function toAppPath(candidate: unknown): string {
  if (typeof candidate !== "string" || !candidate.startsWith("/")) {
    return "/";
  }
  try {
    const url = new URL(candidate, PATH_NORMALIZATION_ORIGIN);
    if (url.origin !== PATH_NORMALIZATION_ORIGIN) {
      return "/";
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "/";
  }
}

/** `undefined` means storage is unavailable, as opposed to the key being absent. */
function readSessionStorage(key: string): string | null | undefined {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return undefined;
  }
}

function writeSessionStorage(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Callers that depend on persistence verify the write by reading it back.
  }
}

function removeSessionStorage(key: string) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
}

export function rememberAuthRedirect(redirectTo: string) {
  writeSessionStorage(AUTH_REDIRECT_STORAGE_KEY, toAppPath(redirectTo));
}

export function getRememberedAuthRedirect() {
  return toAppPath(readSessionStorage(AUTH_REDIRECT_STORAGE_KEY));
}

export function clearRememberedAuthRedirect() {
  removeSessionStorage(AUTH_REDIRECT_STORAGE_KEY);
}

/**
 * The destination an auth round trip should return to: an explicit candidate
 * (the `returnTo` / `redirect` search param) when it points somewhere other
 * than the app root, else the per-tab remembered redirect, else `/`.
 *
 * The result always goes through `toAppPath()`. Search params are already
 * normalized by the routes' `validateSearch`, but the remembered value comes
 * back out of sessionStorage, which anything else running in this origin can
 * write — and both feed navigation.
 *
 * Clearing the remembered redirect is deliberately *not* part of this helper:
 * `callback.tsx` resolves during render (a ref latch evaluated on every
 * render), where a storage write would be a render side effect, and it must
 * stay resolvable while provisioning retries. Call
 * `clearRememberedAuthRedirect()` explicitly where the destination is actually
 * consumed (see `login.tsx`).
 */
export function resolveAuthRedirectDestination(candidate: string | null | undefined): string {
  const explicit = toAppPath(candidate);
  return explicit === "/" ? getRememberedAuthRedirect() : explicit;
}

// Latched when the cooldown marker demonstrably does not persist (write not
// readable back). Without a persisted marker a reload could loop, so the
// automatic reload is disabled for the rest of this document; the ladder's next
// rung (sign-out) still gets the user unstuck.
let reloadRecoveryPersistenceBroken = false;

function parseTimestamp(value: string | null): number | null {
  if (value === null || value.length === 0) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Whether an automatic reload-recovery may run now. Fails closed without persistence. */
export function canAttemptReloadRecovery(now = Date.now()) {
  if (reloadRecoveryPersistenceBroken) {
    return false;
  }
  const persisted = readSessionStorage(AUTH_RELOAD_RECOVERY_STORAGE_KEY);
  if (persisted === undefined) {
    return false;
  }
  return now - (parseTimestamp(persisted) ?? 0) >= RELOAD_RECOVERY_COOLDOWN_MS;
}

/**
 * Stamp the cooldown marker for a reload that is about to start.
 *
 * @returns whether the marker persisted. `false` means the next document would
 * not see it, so an *automatic* reload must not run (it could loop); callers
 * fall through to the ladder's next rung instead.
 */
export function rememberReloadRecovery(now = Date.now()): boolean {
  const marker = String(now);
  writeSessionStorage(AUTH_RELOAD_RECOVERY_STORAGE_KEY, marker);
  const persisted = readSessionStorage(AUTH_RELOAD_RECOVERY_STORAGE_KEY) === marker;
  if (!persisted) {
    reloadRecoveryPersistenceBroken = true;
  }
  return persisted;
}

/**
 * Full document navigation (not an SPA `navigate()`): rebuilds AuthKit and the
 * Convex client from scratch. This is the only in-app action that recovers a
 * wedged client stack, which is why it is kept behind one helper that tests
 * can stub.
 */
export function reloadDocument(path: string) {
  window.location.assign(toAppPath(path));
}
